import React from 'react';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import {
  APPLICATION_METHODS,
  EMPLOYMENT_TYPES,
  SENIORITY_OPTIONS,
  countWords,
  seniorityFromSelection,
} from './wizardConstants';
import { useJobWizard } from './JobWizardContext';

function formatSalary(min, max, currency, period) {
  const nf = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
  const periodLabel = {
    yearly: 'per year',
    monthly: 'per month',
    hourly: 'per hour',
    project: 'per project',
  }[period] || period;
  return `${currency} ${nf.format(Number(min))} – ${nf.format(Number(max))} ${periodLabel}`;
}

export default function Step5ReviewSubmit() {
  const {
    form,
    categories,
    packages,
    totalCredits,
    selectedPackageId,
    setSelectedPackageId: onSelectPackage,
    stepErrors: errors,
    submitting,
    handleSubmit: onSubmit,
  } = useJobWizard();
  const category = categories.find((c) => String(c.id) === String(form.category_id));
  const employment = EMPLOYMENT_TYPES.find((e) => e.value === form.employment_type);
  const seniority = seniorityFromSelection(form.senioritySelection)
    .map((v) => SENIORITY_OPTIONS.find((o) => o.value === v)?.label || v)
    .join(' / ');
  const method = APPLICATION_METHODS.find((m) => m.value === form.application_method);
  const wordCount = countWords(form.description);
  const hasCredits = totalCredits > 0;
  const showTrialNotice = totalCredits === 1 && packages.length > 0 && packages[0].plan === 'trial';

  return (
    <div className="rwm-wizard-step">
      <h2 className="rwm-wizard-step__title">Review &amp; Submit</h2>
      <p className="rwm-wizard-step__intro text-muted">
        Confirm your listing details. Submitting uses one listing credit and sends the job for manual
        review.
      </p>

      <dl className="rwm-wizard-step__summary mb-4">
        <dt>Title</dt>
        <dd data-testid="rwm-wizard-5-summary-title">{form.title || '—'}</dd>
        <dt>Category</dt>
        <dd data-testid="rwm-wizard-5-summary-category">{category?.name || '—'}</dd>
        <dt>Employment</dt>
        <dd>{employment?.label || '—'}</dd>
        <dt>Seniority</dt>
        <dd>{seniority || '—'}</dd>
        <dt>Salary</dt>
        <dd data-testid="rwm-wizard-5-summary-salary">
          {form.salary_min && form.salary_max
            ? formatSalary(form.salary_min, form.salary_max, form.salary_currency, form.salary_period)
            : '—'}
        </dd>
        <dt>Hiring remotely from</dt>
        <dd>{form.location_country || form.location_eligibility || '—'}</dd>
        {form.timezone_requirement ? (
          <>
            <dt>Timezone</dt>
            <dd>{form.timezone_requirement}</dd>
          </>
        ) : null}
        <dt>Description</dt>
        <dd>
          {wordCount} words
        </dd>
        <dt>Application</dt>
        <dd>
          {method?.label || '—'}
          {form.application_method === 'external_url' && form.application_url
            ? ` — ${form.application_url}`
            : null}
          {form.application_method === 'email' && form.application_email
            ? ` — ${form.application_email}`
            : null}
        </dd>
      </dl>

      {!hasCredits ? (
        <Alert variant="warning" data-testid="rwm-wizard-5-no-credits">
          You have no active listing credits. Purchase a package before submitting.
        </Alert>
      ) : (
        <Form.Group className="mb-4">
          <Form.Label>Listing credit (FIFO — oldest package used first)</Form.Label>
          {packages.map((pkg, index) => (
            <Form.Check
              key={pkg.id}
              type="radio"
              name="package_id"
              id={`rwm-wizard-5-package-${pkg.id}`}
              label={`${pkg.plan} — ${pkg.credits_remaining} credit${pkg.credits_remaining === 1 ? '' : 's'} remaining`}
              checked={selectedPackageId === pkg.id}
              onChange={() => onSelectPackage(pkg.id)}
              data-testid={`rwm-wizard-5-package-${pkg.id}`}
              disabled={index > 0}
            />
          ))}
          {packages.length > 0 && selectedPackageId === packages[0].id ? (
            <p className="text-muted small mb-0">The oldest active package will be charged on submit.</p>
          ) : null}
          {showTrialNotice ? (
            <Alert variant="info" data-testid="rwm-wizard-5-trial-notice" className="mt-3">
              You are using your 1 free trial listing. Purchase credits after submission for future
              listings.
            </Alert>
          ) : null}
        </Form.Group>
      )}

      {errors.submit ? (
        <Alert variant="danger" className="mb-3" data-testid="rwm-wizard-5-submit-error">
          {errors.submit}
        </Alert>
      ) : null}

      <Button
        variant="info"
        disabled={!hasCredits || submitting}
        onClick={onSubmit}
        data-testid="rwm-wizard-5-submit"
      >
        {submitting ? (
          <>
            <Spinner animation="border" size="sm" className="me-2" />
            Submitting…
          </>
        ) : (
          'Submit for review'
        )}
      </Button>
    </div>
  );
}
